import { useFormik } from 'formik';
import { alertNotification, useSafeFormikContext } from 'GenericForms/Helper';
import apiGlobal from 'global/api.global';
import { RootState } from 'index';
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Button, Label } from 'reactstrap'
import { AlertColourTypeConstant, AlertTypeConstant, NotificationCategoryList, NotificationPriorityList, Roles } from 'shared/constants'
import { getAuthData } from 'utils/auth/authUtils';
import { useQuery } from 'react-query';
import { queryKeyes } from 'shared/queryKeys';
import { Get } from 'VesselMaster/vesselMaster.hooks';
import { queryClient } from 'react-query/queryClient';
import { renderToStaticMarkup } from "react-dom/server";
import env from 'environment_system/env_system';
import ErrorTooltip from 'Components/ErrorTooltip';
import * as Yup from "yup";
import { addAlert } from 'Store/Generic/ReportingSlice';

const Alert = ({
    alertMessage,
    alertType,
    handlePopUpState,
    fieldId,
    formTitle,
    generatedFor,
    AlertColourType,
    setFieldNull,
    fieldLabel,
    fieldValue,
    sendNotification = true,
    setRefreshKey,
    continueSubmit = false,
    setRefreshFields,
    refreshFieldApplicable = false,
    setRefreshFieldApplicable = false,
    isCreateVesselReport = false,
}: any) => {
    /** State variables */
    const { VesselID, ReportID } = useSelector((state: RootState) => state.Reporting);
    const reportId = ReportID > 0 ? ReportID : null
    const formikContext = useSafeFormikContext<any>();
    const submitForm = formikContext ? formikContext.submitForm : undefined; // Or null
    const dispatch = useDispatch();
    /** State variables end */

    /** Assign initial values to the formik object */
    const getInitialValues = (): any => {
        return {
            alert_message: alertMessage,
            response_text: "",
            response_value: null,
            form_title: formTitle,
            field_label: fieldLabel,
            field_value: fieldValue,
            vessel_reporting_information: reportId,
            vessel: VesselID,
            user: getAuthData('user_id'),
            generated_for: generatedFor
        };
    };

    const validationSchema = Yup.object().shape({
        response_text: Yup.string()
            .trim()
            .required("Response text is required"),
    });

    /** Get categoryId */
    const categoryId = NotificationCategoryList[AlertColourType as keyof typeof NotificationCategoryList];

    /** Get user by role */
    const { data: userVessels }: { data: any[] } = useQuery(
        [queryKeyes.vessel.UserVessel.key],
        async () => await Get(queryKeyes.vessel.UserVessel.url()),
        {
            staleTime: 0,
            enabled: true
        }
    );

    /** Get roleIdArray */
    const roleIdArray = userVessels?.filter(user =>
        user.vessel === VesselID && (
            user.constant === Roles.CHIEFENGINEER ||
            user.constant === Roles.MASTER ||
            user.constant === Roles.ES_ADMIN ||
            user.constant === Roles.MANAGER
        )
    ).map(user => user.user_id);

    /** Check if the roleIdArray is ready */
    const isRoleArrayReady = Array.isArray(roleIdArray) && roleIdArray.length > 0;

    /** Alert's formik object */
    const AlertFormik = useFormik({
        initialValues: getInitialValues(),
        validationSchema,
        onSubmit: () => { },
        enableReinitialize: true,
    })

    /** useEffect */
    useEffect(() => {
        AlertFormik.initialValues = getInitialValues();
        AlertFormik.values = getInitialValues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alertMessage, formTitle, generatedFor, ReportID, VesselID]);
    /** useEffect end */

    /**
     * Determine notification priority based on alert colour type
     */
    const AlertPriorityByColour = {
        [AlertColourTypeConstant.DANGER]: NotificationPriorityList.high,
        [AlertColourTypeConstant.WARNING]: NotificationPriorityList.medium,
        [AlertColourTypeConstant.INFO]: NotificationPriorityList.low,
    } as const;

    /** Get notification priority based on alert colour type */
    const notificationPriority =
        AlertPriorityByColour[AlertColourType as keyof typeof AlertPriorityByColour]
        ?? NotificationPriorityList.low;

    /** Alert POST function */
    const alertSubmit = async (responseValue: string) => {
        if (
            alertType === AlertTypeConstant.WITH_REASON &&
            responseValue === "yes" &&
            !AlertFormik.values.response_text.trim()
        ) {
            AlertFormik.setTouched({ response_text: true });
            await AlertFormik.validateForm();
            return;
        }
        const finalAlertObject = {
            ...getInitialValues(),
            alert_message: renderToStaticMarkup(alertMessage),
            response_value: responseValue,
            response_text: AlertFormik.values.response_text,
            AlertColourType,
            roleIdArray,
            id: Date.now(),
        };

        handlePopUpState();
        if (isCreateVesselReport) {
            if ((responseValue === "yes" || responseValue === "ok") && fieldId !== "reporting_event") {
                await dispatch(addAlert(finalAlertObject));
            }
            return;
        }
        try {
            const res = await apiGlobal.post(`alert_details/`, finalAlertObject);
            if (res?.status === 201 && continueSubmit) {
                submitForm?.();
            }
            return res;
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Row>
            <Col>
                <p style={{ whiteSpace: "pre-line", textAlign: "center" }}>{alertMessage}</p>
                {alertType === AlertTypeConstant.WITH_REASON &&
                    <Label for="response_text" className="form-label">Please provide a reason
                        <>
                            <textarea
                                className="form-control bg-white mb-2"
                                id="response_text"
                                name="response_text"
                                rows={4}
                                onChange={AlertFormik.handleChange}
                                onBlur={AlertFormik.handleChange}
                            />
                            {env?.form_validation &&
                                AlertFormik.touched.response_text &&
                                AlertFormik.errors.response_text && (
                                    <ErrorTooltip
                                        target="response_text"
                                        message={AlertFormik.errors.response_text}
                                        open={!!AlertFormik.errors.response_text}
                                    />
                                )}
                        </>
                    </Label>
                }
                {(alertType === AlertTypeConstant.INFO_ONLY) &&
                    <Button
                        onClick={() => {
                            alertSubmit("ok").then(async (res: any) => {
                                if (res?.status === 201 && sendNotification) {
                                    await alertNotification(AlertFormik.values, roleIdArray, categoryId, VesselID, fieldLabel, fieldValue, reportId, notificationPriority);
                                    await queryClient.invalidateQueries(queryKeyes.notification.NotificationInbox.key);
                                    await queryClient.invalidateQueries(queryKeyes.notification.NotificationForLoggedInUser.key);
                                }
                            })
                        }}
                        type="button"
                        color="primary"
                        className="btn_size_cstm"
                        disabled={!isRoleArrayReady}
                    >
                        OK
                    </Button>
                }
                {(alertType === AlertTypeConstant.WITH_YES_NO_ACTION || alertType === AlertTypeConstant.WITH_REASON) &&
                    <div className="d-flex justify-content-center gap-3">
                        <Button
                            onClick={async () => {
                                await alertSubmit("yes").then(async (res: any) => {
                                    if (res?.status === 201 && sendNotification) {
                                        await alertNotification(AlertFormik.values, roleIdArray, categoryId, VesselID, fieldLabel, fieldValue, reportId, notificationPriority);
                                        await queryClient.invalidateQueries(queryKeyes.notification.NotificationInbox.key);
                                        await queryClient.invalidateQueries(queryKeyes.notification.NotificationForLoggedInUser.key);
                                    }
                                })
                                await document?.getElementById(fieldId)?.blur();
                            }}
                            type="button"
                            color="primary"
                            className="btn_size_cstm"
                            disabled={
                                alertType === AlertTypeConstant.WITH_REASON &&
                                !AlertFormik.values.response_text.trim() &&
                                !isRoleArrayReady
                            }
                        >
                            Yes
                        </Button>
                        <Button
                            onClick={() => {
                                if (refreshFieldApplicable && setRefreshFields) {
                                    setRefreshFields((prev: number) => prev + 1);
                                    setRefreshFieldApplicable(false);
                                }
                                alertSubmit("no");
                                if (setFieldNull) {
                                    setFieldNull();
                                }
                                if (setRefreshKey) {
                                    setRefreshKey((prevKey: number) => prevKey + 1)
                                }
                                document?.getElementById(fieldId)?.focus();
                            }}
                            type="button"
                            color="danger"
                            className="btn_size_cstm"
                        >
                            No
                        </Button>
                    </div>
                }
            </Col>
        </Row>
    )
}

export default Alert